import { ROUTE_PATHS, corridorPath } from "@/routes/routePaths";

export interface CityHubRoute {
  label: string;
  path: string;
  distance?: string;
  description: string;
}

export interface CityHub {
  slug: string;
  city: string;
  eyebrow: string;
  title: string;
  overview: string;
  dailyCommute?: {
    title: string;
    description: string;
    zones: string[];
    peakTimes: string;
    savingsExample: string;
    commutePath: string;
  };
  popularRoutes: CityHubRoute[];
  localReasons: string[];
  relatedHubs: { label: string; path: string }[];
}

export const CITY_HUBS: Record<string, CityHub> = {
  "hyderabad-carpool": {
    slug: "hyderabad-carpool",
    city: "Hyderabad",
    eyebrow: "City hub",
    title: "Hyderabad carpool — daily commute & intercity rides",
    overview:
      "Hyderabad's sprawl from Gachibowli to Uppal makes solo driving expensive and slow. Duri connects IT corridors, Old City families, and weekend travellers heading to Vijayawada, Guntur, and Bengaluru. Hosts list seats on routes they already drive — so office carpools and intercity trips share the same verified network.",
    dailyCommute: {
      title: "Daily office commute in Hyderabad",
      description:
        "The highest demand is Hitech City ↔ Gachibowli ↔ Financial District, plus Miyapur and LB Nagar feeds into ORR offices. Most commuters book recurring morning (8–9 AM) and evening (6–8 PM) slots Monday–Friday.",
      zones: ["Hitech City", "Gachibowli", "Financial District", "Madhapur", "Kondapur", "Miyapur", "LB Nagar", "Uppal"],
      peakTimes: "7:30–9:30 AM outbound · 6:00–8:30 PM return",
      savingsExample: "A Madhapur → Financial District commuter typically saves ₹4,000–₹6,000/month vs cab or own-car fuel + parking.",
      commutePath: ROUTE_PATHS.HYDERABAD_DAILY_COMMUTE_CARPOOL,
    },
    popularRoutes: [
      {
        label: "Hyderabad → Vijayawada",
        path: corridorPath("hyderabad-vijayawada"),
        distance: "275 km",
        description: "NH65 corridor — busiest intercity route from Hyderabad.",
      },
      {
        label: "Hyderabad → Guntur",
        path: corridorPath("hyderabad-guntur"),
        distance: "285 km",
        description: "Popular with families and students in the Amaravati belt.",
      },
      {
        label: "Hyderabad → Bangalore",
        path: corridorPath("hyderabad-bangalore"),
        distance: "570 km",
        description: "NH44 tech corridor — overnight and weekend pools.",
      },
      {
        label: "Hyderabad → Khammam",
        path: corridorPath("hyderabad-khammam"),
        distance: "195 km",
        description: "Short NH65 hop for family and work travel.",
      },
    ],
    localReasons: [
      "ORR tolls and fuel make solo drives costly — splitting with 3 co-travellers cuts trip cost sharply.",
      "Metro doesn't reach every tech park; carpools fill the last-mile gap from residential hubs.",
      "Verified Aadhaar profiles matter in a city where many commuters are new to town each quarter.",
      "Women-preferred filters are widely used on Hitech City and Gachibowli morning runs.",
    ],
    relatedHubs: [
      { label: "Intercity ridesharing in India", path: ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA },
      { label: "Daily commute carpool", path: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL },
      { label: "Vijayawada ridesharing", path: ROUTE_PATHS.VIJAYAWADA_RIDESHARING },
    ],
  },
  "vijayawada-ridesharing": {
    slug: "vijayawada-ridesharing",
    city: "Vijayawada",
    eyebrow: "City hub",
    title: "Vijayawada ridesharing — local & intercity carpools",
    overview:
      "Vijayawada sits at the crossroads of Andhra Pradesh — Benz Circle, Auto Nagar, and the Krishna riverfront feed daily traffic to Hyderabad, Guntur, Nellore, and the coast. Duri hosts include professionals who drive to Hyderabad twice a week and students heading to capital-region colleges.",
    dailyCommute: {
      title: "Daily commute in Vijayawada",
      description:
        "Intracity demand clusters around Benz Circle, PVP Mall, and Kanaka Durga Temple access roads. Short hops to Guntur and Tenali appear on weekday mornings for college and hospital staff.",
      zones: ["Benz Circle", "Auto Nagar", "PVP Mall", "Kanaka Durga Temple", "Eluru Road", "Governorpet"],
      peakTimes: "7:00–9:00 AM · 5:30–7:30 PM",
      savingsExample: "Vijayawada → Guntur daily riders report ₹2,500+/month savings vs dedicated cab contracts.",
      commutePath: ROUTE_PATHS.VIJAYAWADA_DAILY_COMMUTE_CARPOOL,
    },
    popularRoutes: [
      {
        label: "Vijayawada → Hyderabad",
        path: `${ROUTE_PATHS.INTERCITY_ROUTES}/vijayawada-to-hyderabad-carpool`,
        distance: "275 km",
        description: "Return leg of NH65 — strong Sunday evening supply.",
      },
      {
        label: "Vijayawada → Guntur",
        path: corridorPath("vijayawada-guntur"),
        distance: "35 km",
        description: "High-frequency short hop — daily micro-carpools.",
      },
      {
        label: "Vijayawada → Visakhapatnam",
        path: corridorPath("vijayawada-visakhapatnam"),
        distance: "350 km",
        description: "Coastal Andhra on NH16.",
      },
      {
        label: "Vijayawada → Nellore",
        path: corridorPath("vijayawada-nellore"),
        distance: "280 km",
        description: "NH16 toward Chennai belt.",
      },
    ],
    localReasons: [
      "RTC bus queues at Pandit Nehru Bus Station spike on festival weekends — carpools offer seat certainty.",
      "Medical travel to Hyderabad hospitals is a steady weekday use case.",
      "Kanaka Durga temple seasons bring family groups that prefer verified sedans over crowded buses.",
      "Students at VIT-AP and SRM-AP often rideshare to Hyderabad for weekends.",
    ],
    relatedHubs: [
      { label: "Intercity ridesharing in India", path: ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA },
      { label: "Hyderabad carpool", path: ROUTE_PATHS.HYDERABAD_CARPOOL },
      { label: "Chennai ridesharing", path: ROUTE_PATHS.CHENNAI_RIDESHARING },
    ],
  },
  "bangalore-carpool": {
    slug: "bangalore-carpool",
    city: "Bangalore",
    eyebrow: "City hub",
    title: "Bangalore carpool — daily commute & intercity rides",
    overview:
      "Bengaluru's ORR and tech-park sprawl makes solo commuting slow and expensive. Duri connects Whitefield, Electronic City, and Koramangala commuters with hosts on daily office routes — and with the busy overnight Hyderabad ↔ Bengaluru highway corridor.",
    dailyCommute: {
      title: "Daily office commute in Bengaluru",
      description:
        "Peak demand runs Whitefield ↔ Electronic City, Marathahalli ↔ Manyata, and ORR loops. Recurring weekday pools are common among IT teams in the same campus.",
      zones: ["Whitefield", "Electronic City", "Koramangala", "Manyata", "Marathahalli", "HSR Layout"],
      peakTimes: "8:00–10:00 AM · 6:00–8:30 PM",
      savingsExample: "Whitefield → Electronic City commuters save ₹5,000+/month vs cab or fuel+parking solo.",
      commutePath: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL,
    },
    popularRoutes: [
      {
        label: "Hyderabad → Bangalore",
        path: corridorPath("hyderabad-bangalore"),
        distance: "570 km",
        description: "India's busiest tech highway on NH44.",
      },
      {
        label: "Bangalore → Hyderabad",
        path: `${ROUTE_PATHS.INTERCITY_ROUTES}/bangalore-to-hyderabad-carpool`,
        distance: "570 km",
        description: "Return pools for weekend home travel.",
      },
      {
        label: "Bengaluru → Chittoor",
        path: corridorPath("bangalore-chittoor"),
        distance: "165 km",
        description: "Border corridor on NH75 — family & campus travel.",
      },
      {
        label: "Chittoor → Bengaluru",
        path: `${ROUTE_PATHS.INTERCITY_ROUTES}/chittoor-to-bangalore-carpool`,
        distance: "165 km",
        description: "Return seats toward Bengaluru tech parks.",
      },
    ],
    localReasons: [
      "ORR congestion rewards full cars — four verified commuters beat four solo drives.",
      "Many hosts on Hyderabad route already work in Bengaluru tech parks.",
      "Women-preferred filters are popular on early-morning Whitefield departures.",
      "Overnight NH44 pools recover fuel cost for hosts who drive anyway.",
    ],
    relatedHubs: [
      { label: "Intercity ridesharing in India", path: ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA },
      { label: "Hyderabad carpool", path: ROUTE_PATHS.HYDERABAD_CARPOOL },
      { label: "Daily commute carpool", path: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL },
    ],
  },
  "chennai-ridesharing": {
    slug: "chennai-ridesharing",
    city: "Chennai",
    eyebrow: "City hub",
    title: "Chennai ridesharing — commute, Tirupati & coastal routes",
    overview:
      "Chennai commuters use Duri for OMR and GST Road office pools, Nellore connections on NH16, and high-demand Tirupati pilgrimage weekends. Verified hosts list seats on routes they already drive.",
    dailyCommute: {
      title: "Daily commute in Chennai",
      description:
        "OMR, Velachery, and Tambaram feeds dominate morning demand toward IT corridors. Short pools reduce parking stress at campus gates.",
      zones: ["OMR", "Velachery", "Tambaram", "Chromepet", "Kilpauk", "Anna Nagar"],
      peakTimes: "8:00–10:00 AM · 6:00–8:00 PM",
      savingsExample: "OMR corridor commuters typically save ₹3,500–₹5,000/month carpooling vs solo cab.",
      commutePath: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL,
    },
    popularRoutes: [
      {
        label: "Nellore → Chennai",
        path: corridorPath("nellore-chennai"),
        distance: "180 km",
        description: "NH16 into the metro — students and IT staff.",
      },
      {
        label: "Chennai → Nellore",
        path: `${ROUTE_PATHS.INTERCITY_ROUTES}/chennai-to-nellore-carpool`,
        distance: "180 km",
        description: "Return coastal highway pools.",
      },
      {
        label: "Chennai → Tirupati",
        path: corridorPath("chennai-tirupati"),
        distance: "135 km",
        description: "Weekend darshan — book early on festival weeks.",
      },
      {
        label: "Tirupati → Chennai",
        path: `${ROUTE_PATHS.INTERCITY_ROUTES}/tirupati-to-chennai-carpool`,
        distance: "135 km",
        description: "Sunday return after temple visits.",
      },
    ],
    localReasons: [
      "Tirupati festival weeks spike cab fares — carpools keep per-seat pricing predictable.",
      "Nellore–Chennai is a daily student and patient corridor on NH16.",
      "OMR parking is limited; shared rides reduce cars at campus gates.",
      "Verified profiles matter for women on early-morning pilgrimage travel.",
    ],
    relatedHubs: [
      { label: "Intercity ridesharing in India", path: ROUTE_PATHS.INTERCITY_RIDESHARING_INDIA },
      { label: "Vijayawada ridesharing", path: ROUTE_PATHS.VIJAYAWADA_RIDESHARING },
      { label: "Daily commute carpool", path: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL },
    ],
  },
};

export function getCityHub(slug: string): CityHub | undefined {
  return CITY_HUBS[slug];
}
